import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOnboarding, type OnboardingResponse } from '@/lib/rpc/user/onboarding';
import { isAdminSurface } from '@/lib/routing/isAdminSurface';
import { normalizeNextRoute } from '@/lib/routing/onboardingRouteMap';

export type OnboardingStatusResponse = {
  request_id: string;
  state: 'queued' | 'running' | 'ready' | 'failed' | 'unknown';
  updated_at?: string;
  last_error?: string | null;
  metadata?: {
    customer_id?: string;
    bridge_customer_id?: string;
    processed_at?: string;
    [key: string]: any;
  };
};

function followRoute(route: string, navigate: ReturnType<typeof useNavigate>, replace: boolean = true) {
  if (!route) return;
  if (typeof window !== 'undefined' && window.location?.pathname === route) {
    return;
  }
  navigate(route, { replace });
}

export function useOnboardingStatus() {
  const [isPolling, setIsPolling] = useState(false);
  const navigate = useNavigate();

  const redirectProductIfVerified = useCallback(async (): Promise<boolean> => {
    try {
      const response = await getOnboarding();

      const normalizedRoute = normalizeNextRoute(response.next_route);

      if (response.state === 'approved' || normalizedRoute === '/dashboard') {
        followRoute(normalizedRoute, navigate);
        return true;
      }

      return false;
    } catch (error) {
      console.warn('[useOnboardingStatus] redirectProductIfVerified failed', error);
      return false;
    }
  }, [navigate]);

  const startOnboarding = useCallback(async (): Promise<OnboardingResponse> => {
    const response = await getOnboarding();

    const normalizedRoute = normalizeNextRoute(response.next_route);

    if (normalizedRoute) {
      followRoute(normalizedRoute, navigate, response.state !== 'approved');
    }

    return response;
  }, [navigate]);

  const checkStatus = useCallback(async (requestId: string): Promise<OnboardingStatusResponse> => {
    return {
      request_id: requestId,
      state: 'ready',
      updated_at: new Date().toISOString()
    };
  }, []);

  const pollStatus = useCallback(async (
    requestId: string,
    onReady: () => void,
    maxDuration: number = 2 * 60 * 1000
  ) => {
    setIsPolling(true);
    const stopAt = Date.now() + maxDuration;

    const poll = async () => {
      try {
        if (Date.now() >= stopAt) {
          setIsPolling(false);
          return;
        }

        const status = await checkStatus(requestId);

        if (status.state === 'ready') {
          setIsPolling(false);
          onReady();
          return;
        }

        if (status.state === 'failed') {
          setIsPolling(false);
          return;
        }

        setTimeout(poll, 5000);
      } catch (error) {
        console.error('[useOnboardingStatus] Polling error:', error);
        setIsPolling(false);
      }
    };

    poll();
  }, [checkStatus]);

  return {
    startOnboarding,
    checkStatus,
    pollStatus,
    isPolling,
    redirectProductIfVerified
  };
}
